"use client";
import { useEffect, useRef } from "react";

/**
 * ScrollProgress — duenner Top-Progress-Bar (Brand-Gradient).
 *
 * Reine Dekoration: role=presentation + aria-hidden=true; weder
 * Screenreader noch Tastatur-Nutzer interagieren damit. Passive
 * Scroll-Listener und rAF-Throttle halten Main-Thread sauber.
 * prefers-reduced-motion: Bar bleibt sichtbar, springt aber ohne
 * Transition zur Position (keine smooth-Verfolgung).
 */
export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const bar = barRef.current;
    if (!bar) return;

    let raf = 0;
    let pending = false;
    const update = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, h.scrollTop / max)) : 0;
      bar.style.transform = `scaleX(${p.toFixed(4)})`;
      pending = false;
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="lp-scroll-progress"
      role="presentation"
      aria-hidden="true"
    >
      <div ref={barRef} className="lp-scroll-progress-bar" />
    </div>
  );
}
